import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import type { SupportedLanguage } from '@/components/multilingual/LanguageSelector';
import {
  subprocessors,
  subprocessorsCopy,
  SUBPROCESSOR_LIST_UPDATED,
} from '@/lib/i18n/subprocessors';

interface SubprocessorListProps {
  language: SupportedLanguage;
}

/**
 * Sub-processor and recipient disclosure (Art. 13(1)(e) GDPR).
 *
 * Rendered as a structured React section inside the Privacy Policy — the
 * policy body itself is translated HTML, so keeping the provider table typed
 * here avoids flattening it into translation strings.
 */
const SubprocessorList = ({ language }: SubprocessorListProps) => {
  const t = subprocessorsCopy[language] ?? subprocessorsCopy.en;

  return (
    <section id="sub-processors" className="mt-10 not-prose">
      <h2 className="text-2xl font-bold mb-2">{t.title}</h2>
      <p className="text-sm text-muted-foreground mb-4">
        {t.updated}: {SUBPROCESSOR_LIST_UPDATED}
      </p>
      <p className="text-foreground/80 mb-6 leading-relaxed">{t.intro}</p>

      <div className="space-y-4">
        {subprocessors.map((sp) => (
          <Card key={sp.key}>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h3 className="text-lg font-semibold m-0">{sp.provider}</h3>
                <Badge variant={sp.processor ? 'secondary' : 'outline'}>
                  {sp.processor ? t.processorTag : t.recipientTag}
                </Badge>
              </div>
              <dl className="grid gap-3 sm:grid-cols-2 text-sm">
                <div>
                  <dt className="font-medium text-muted-foreground">{t.colPurpose}</dt>
                  <dd className="text-foreground/90">{sp.purpose[language]}</dd>
                </div>
                <div>
                  <dt className="font-medium text-muted-foreground">{t.colLocation}</dt>
                  <dd className="text-foreground/90">{sp.location[language]}</dd>
                </div>
                <div>
                  <dt className="font-medium text-muted-foreground">{t.colData}</dt>
                  <dd className="text-foreground/90">{sp.data[language]}</dd>
                </div>
                <div>
                  <dt className="font-medium text-muted-foreground">{t.colBasis}</dt>
                  <dd className="text-foreground/90">{sp.basis[language]}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        ))}
      </div>

      <h3 className="text-lg font-semibold mt-8 mb-2">{t.howWeChange}</h3>
      <p className="text-foreground/80 leading-relaxed">{t.howWeChangeBody}</p>
      <p className="mt-4 text-sm text-muted-foreground">{t.contact}</p>
    </section>
  );
};

export default SubprocessorList;
